import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Leaf, Phone, User, LogOut } from "lucide-react";
import { Button } from "./ui/button";
import { brand } from "../mock";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";

const links = [
  { to: "/", label: "Home" },
  { to: "/plans", label: "Plans" },
  { to: "/menu", label: "Menu" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-40 w-full border-b border-transparent bg-white/85 backdrop-blur transition-shadow ${
        scrolled ? "shadow-[0_6px_20px_-12px_rgba(0,0,0,0.15)] border-neutral-200" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <span className="w-9 h-9 rounded-full bg-green-600 text-white flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
            <Leaf size={18} />
          </span>
          <span className="font-extrabold tracking-tight text-lg text-neutral-900">
            {brand.name}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `nav-link text-sm font-semibold text-neutral-700 hover:text-green-700 ${
                  isActive ? "active text-green-700" : ""
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${brand.phone}`}
            className="hidden lg:flex items-center gap-2 text-sm text-neutral-600 hover:text-green-700"
          >
            <Phone size={14} /> {brand.phone}
          </a>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 h-9 rounded-full border border-neutral-300 hover:border-green-600 hover:text-green-700 text-sm font-semibold">
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt=""
                      className="w-6 h-6 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs flex items-center justify-center">
                      {(user.name || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="max-w-[110px] truncate pr-1">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-xs text-neutral-500 font-normal truncate">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/account" className="cursor-pointer">
                    <User size={14} className="mr-2" /> My Account
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600 focus:text-red-600">
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login">
              <Button variant="outline" className="rounded-full border-neutral-300 h-9 px-4">
                Sign In
              </Button>
            </Link>
          )}
          <Link to="/plans">
            <Button className="bg-green-600 hover:bg-green-700 text-white rounded-full px-5">
              Order Now
            </Button>
          </Link>
        </div>

        <button
          className="md:hidden p-2 rounded-md hover:bg-neutral-100"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-neutral-200 bg-white">
          <div className="px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-semibold ${
                    isActive
                      ? "bg-green-50 text-green-700"
                      : "text-neutral-700 hover:bg-neutral-100"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/plans" className="mt-2">
              <Button className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full">
                Order Now
              </Button>
            </Link>
            {user ? (
              <>
                <Link to="/account">
                  <Button variant="outline" className="w-full mt-2 rounded-full border-neutral-300">
                    <User size={14} className="mr-1" /> My Account
                  </Button>
                </Link>
                <Button onClick={logout} variant="outline" className="w-full mt-2 rounded-full border-neutral-300 text-red-600 hover:text-red-600">
                  <LogOut size={14} className="mr-1" /> Sign out
                </Button>
              </>
            ) : (
              <Link to="/login">
                <Button variant="outline" className="w-full mt-2 rounded-full border-neutral-300">
                  Sign In
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
